import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSnapshot, getDrafts, saveImageDraft, deleteImageDraft, uploadDraftMedia, copyScreenshots,
  type ImageType, type DraftImageItem,
} from '@/api/play';
import { MediaUpload } from '../MediaUpload';
import { ConfirmModal } from './ConfirmModal';
import { Button } from '../Button';
import { LocalizeScreenshotsModal } from './LocalizeScreenshotsModal';
import { GenerateScreenshotsModal, playShotTarget, playFeatureTarget } from './GenerateScreenshotsModal';
import { CopyScreenshotsBar } from './CopyScreenshotsBar';
import { sourceScreenshots } from '@/lib/playScreenshots';
import {
  prepareImageUpload, PLAY_FEATURE_GRAPHIC, PLAY_ICON, PLAY_SCREENSHOT, type ImageTarget,
} from '@/lib/image-upload';

// Every slot has a geometry Play insists on. Uploads are resized and compressed
// to it in the browser, so an oversized drop is fixed instead of rejected.
const UPLOAD_TARGETS: Record<ImageType, ImageTarget> = {
  icon: PLAY_ICON,
  featureGraphic: PLAY_FEATURE_GRAPHIC,
  phoneScreenshots: PLAY_SCREENSHOT,
};

// Draft-first: every operation saves the desired final state to the server
// draft (D1 + R2). Nothing reaches Google until the draft is submitted.

export function ImagesPanel({ appId, lang }: { appId: number; lang: string }) {
  return (
    <div className="mt-6 border-t border-border pt-4 space-y-6">
      <ImageSection
        appId={appId} lang={lang} imageType="icon" label="App icon" single
        hint="512×512 PNG — larger images are resized. Uploading replaces the current icon."
      />
      <ImageSection
        appId={appId} lang={lang} imageType="featureGraphic" label="Feature graphic" single
        hint="1024×500 — larger images are resized. Uploading replaces the current graphic."
      />
      <ImageSection
        appId={appId} lang={lang} imageType="phoneScreenshots" label="Phone screenshots" single={false}
        hint="PNG/JPEG, up to 8 — oversized images are resized and compressed. Drag to reorder."
      />
    </div>
  );
}

function itemKey(it: DraftImageItem): string {
  return it.kind === 'live' ? `l:${it.id}` : `m:${it.key}`;
}

function ImageSection({ appId, lang, imageType, label, hint, single }: {
  appId: number; lang: string; imageType: ImageType; label: string; hint: string; single: boolean;
}) {
  const qc = useQueryClient();
  // Live media comes from the shared per-app snapshot (one Play edit for the
  // whole editor). React Query dedupes this across all image sections + the
  // listing editor, so the page costs a single snapshot fetch.
  const { data: snapshot, isLoading: liveLoading, error: liveError } = useQuery({
    queryKey: ['play-snapshot', appId],
    queryFn: () => getSnapshot(appId),
  });
  const live = snapshot?.images?.[lang]?.[imageType];
  const { data: drafts, isLoading: draftsLoading } = useQuery({
    queryKey: ['play-drafts', appId],
    queryFn: () => getDrafts(appId),
  });

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  const [showLocalize, setShowLocalize] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);

  const draft = (drafts?.images ?? []).find((d) => d.language === lang && d.imageType === imageType);
  const liveImages = live ?? [];
  const liveUrlById = new Map(liveImages.map((i) => [i.id, i.url]));
  const items: DraftImageItem[] = draft ? draft.items : liveImages.map((i) => ({ kind: 'live', id: i.id }));
  const isDraft = !!draft;
  const max = single ? 1 : 8;

  const invalidateDrafts = () => qc.invalidateQueries({ queryKey: ['play-drafts', appId] });

  // Source locale + its phone screenshots (live ∪ draft), for the "Localize
  // screenshots" flow. Memoized so the array reference is stable across renders
  // (LocalizeScreenshotsModal's OCR effect depends on it — a fresh array each
  // render would re-OCR and wipe in-progress caption edits).
  const localizeSource = useMemo<{ source: string | null; items: { key: string; url: string }[] }>(() => {
    if (imageType !== 'phoneScreenshots') return { source: null, items: [] };
    const listingLangs = (snapshot?.listings ?? []).map((l) => l.language);
    const source = listingLangs.find((l) => l === 'en-US')
      ?? listingLangs.find((l) => l.startsWith('en'))
      ?? listingLangs[0];
    if (!source || source === lang) return { source: null, items: [] };
    return { source, items: sourceScreenshots(snapshot, drafts, source) };
  }, [imageType, lang, snapshot, drafts]);

  // The target locale's current screenshots as (DraftImageItem, url) pairs,
  // positionally aligned with the source. The modal keeps untouched slots as-is
  // on save, so the item's kind must be preserved (a live reference stays live).
  // Derived from the stable snapshot/drafts refs (not the per-render `items`) so
  // the reference stays stable — the modal's OCR effect depends on it.
  const localizeTarget = useMemo<{ item: DraftImageItem; url: string }[]>(() => {
    if (imageType !== 'phoneScreenshots') return [];
    const tLive = snapshot?.images?.[lang]?.phoneScreenshots ?? [];
    const tDraft = (drafts?.images ?? []).find((d) => d.language === lang && d.imageType === 'phoneScreenshots');
    const tUrlById = new Map(tLive.map((i) => [i.id, i.url]));
    const src: DraftImageItem[] = tDraft ? tDraft.items : tLive.map((i) => ({ kind: 'live', id: i.id }));
    return src.map((it) => ({
      item: it,
      url: it.kind === 'live' ? (tUrlById.get(it.id) ?? '') : (it.url ?? ''),
    }));
  }, [imageType, lang, snapshot, drafts]);

  // Listing text of this locale for the AI screenshot planner: the draft is
  // what the operator is actually shipping, so it wins over the live listing.
  const listingText = useMemo(() => {
    const d = (drafts?.listings ?? []).find((l) => l.language === lang);
    const live = (snapshot?.listings ?? []).find((l) => l.language === lang);
    const src = d ?? live;
    return {
      title: src?.title ?? '',
      shortDescription: src?.shortDescription ?? '',
      fullDescription: src?.fullDescription ?? '',
    };
  }, [snapshot, drafts, lang]);

  // Whether this locale already has a feature graphic (draft wins over live):
  // a set generated for a locale without one draws the banner with it.
  const featureExists = useMemo(() => {
    const d = (drafts?.images ?? []).find((x) => x.language === lang && x.imageType === 'featureGraphic');
    return d ? d.items.length > 0 : (snapshot?.images?.[lang]?.featureGraphic?.length ?? 0) > 0;
  }, [drafts, snapshot, lang]);
  const featureTarget = useMemo(
    () => playFeatureTarget(appId, lang, featureExists, listingText),
    [appId, lang, featureExists, listingText],
  );

  const shotTarget = useMemo(
    () => playShotTarget(appId, lang, items, listingText, featureTarget),
    // `items` is rebuilt every render; the dialog snapshots it on open anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appId, lang, draft, live, listingText, featureTarget],
  );

  // The feature graphic slot's own AI button: a banner on its own, planned
  // from the brand pack and the listing (no screenshot set to borrow a style from).
  const [bannerBusy, setBannerBusy] = useState(false);
  const drawBanner = async () => {
    setBannerBusy(true); setActionError(null);
    try {
      await featureTarget.draw({ use_brand: true, ui_refs: false });
      invalidateDrafts();
    } catch (e: any) {
      setActionError(e?.message ?? 'Banner generation failed');
    } finally {
      setBannerBusy(false);
    }
  };

  // Every locale's screenshot count (draft wins over live), for the copy bar:
  // where a set can be pulled from, and how many locales "all" would write.
  const { copySources, otherLocales } = useMemo(() => {
    if (imageType !== 'phoneScreenshots') return { copySources: [], otherLocales: 0 };
    const listingLangs = new Set<string>([
      ...(snapshot?.listings ?? []).map((l) => l.language),
      ...(drafts?.listings ?? []).map((l) => l.language),
    ]);
    const all = new Set<string>([...listingLangs, ...Object.keys(snapshot?.images ?? {})]);
    for (const d of drafts?.images ?? []) if (d.imageType === 'phoneScreenshots') all.add(d.language);
    const countOf = (l: string) => {
      const d = (drafts?.images ?? []).find((x) => x.language === l && x.imageType === 'phoneScreenshots');
      return d ? d.items.length : (snapshot?.images?.[l]?.phoneScreenshots ?? []).length;
    };
    return {
      copySources: [...all].filter((l) => l !== lang).sort()
        .map((l) => ({ lang: l, count: countOf(l) })).filter((s) => s.count > 0),
      otherLocales: [...listingLangs].filter((l) => l !== lang).length,
    };
  }, [imageType, lang, snapshot, drafts]);

  // Update Batches links here with #screenshots: bring the set into view once loaded.
  const anchorRef = useRef<HTMLDivElement>(null);
  const scrolled = useRef(false);
  useEffect(() => {
    if (imageType !== 'phoneScreenshots' || scrolled.current || liveLoading || draftsLoading) return;
    if (window.location.hash !== '#screenshots') return;
    scrolled.current = true;
    anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [imageType, liveLoading, draftsLoading]);

  const saveMut = useMutation({
    mutationFn: (next: DraftImageItem[]) => saveImageDraft(appId, lang, imageType, next),
    onSuccess: () => { invalidateDrafts(); setActionError(null); },
    onError: (e: any) => setActionError(e?.message ?? 'Failed to save draft'),
  });

  const revertMut = useMutation({
    mutationFn: () => deleteImageDraft(appId, lang, imageType),
    onSuccess: () => { invalidateDrafts(); setConfirmRevert(false); setActionError(null); },
    onError: (e: any) => setActionError(e?.message ?? 'Failed to revert'),
  });

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    saveMut.mutate(next);
  };

  const removeAt = (idx: number) => saveMut.mutate(items.filter((_, i) => i !== idx));

  const uploadTarget = UPLOAD_TARGETS[imageType];

  const onUpload = async (file: File): Promise<string> => {
    setActionError(null);
    try {
      const fitted = await prepareImageUpload(file, uploadTarget);
      const media = await uploadDraftMedia(appId, fitted);
      const newItem: DraftImageItem = { kind: 'media', key: media.key, contentType: fitted.type, url: media.url };
      saveMut.mutate(single ? [newItem] : [...items, newItem]);
    } catch (e: any) {
      setActionError(e?.message ?? 'Upload failed');
    }
    return '';
  };

  // Upload several files in order, appending to the draft up to the 8-slot cap.
  const onFiles = async (files: File[]) => {
    setActionError(null);
    const room = max - items.length;
    const take = files.slice(0, room);
    if (files.length > room) setActionError(`Only ${room} slot(s) left — kept the first ${room} of ${files.length}.`);
    setBatchProgress({ done: 0, total: take.length });
    let next = items;
    for (let i = 0; i < take.length; i++) {
      try {
        const fitted = await prepareImageUpload(take[i], uploadTarget);
        const media = await uploadDraftMedia(appId, fitted);
        next = [...next, { kind: 'media', key: media.key, contentType: fitted.type, url: media.url } as DraftImageItem];
        await saveImageDraft(appId, lang, imageType, next);
      } catch (e: any) {
        setActionError(e?.message ?? `Upload failed on file ${i + 1}`);
      }
      setBatchProgress({ done: i + 1, total: take.length });
    }
    invalidateDrafts();
    setBatchProgress(null);
  };

  const onRevert = () => {
    if (items.some((i) => i.kind === 'media')) setConfirmRevert(true);
    else revertMut.mutate();
  };

  const loading = liveLoading || draftsLoading;
  const overlayBtn = 'bg-black/60 text-white text-xs rounded px-1.5 py-0.5 disabled:opacity-30';
  const imgClass = imageType === 'icon'
    ? 'w-24 h-24 rounded-lg border border-border object-cover'
    : 'h-32 rounded-md border border-border object-cover';

  return (
    <div ref={anchorRef} id={imageType === 'phoneScreenshots' ? 'screenshots' : undefined} className="scroll-mt-4">
      <div className="flex items-baseline gap-2 mb-2">
        <h4 className="text-sm font-medium text-content">{label}</h4>
        <span className="text-xs text-content-muted">{hint}</span>
        {isDraft && (
          <>
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400">draft</span>
            <button className="text-xs text-content-muted underline hover:text-content" onClick={onRevert}>
              Revert to live
            </button>
          </>
        )}
      </div>

      {loading ? (
        <p className="text-content-muted text-sm">Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-3">
            {items.map((it, i) => {
              const url = it.kind === 'live' ? liveUrlById.get(it.id) : it.url;
              return (
                <div
                  key={itemKey(it)}
                  draggable={!single}
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex !== null && dragIndex !== i) move(dragIndex, i);
                    setDragIndex(null);
                  }}
                  onDragEnd={() => setDragIndex(null)}
                  className={`relative group ${single ? '' : 'cursor-grab'} ${dragIndex === i ? 'opacity-50' : ''}`}
                >
                  {url ? (
                    <img src={url} alt={`${label} ${i + 1}`} className={imgClass} />
                  ) : (
                    <div className={`${imgClass} flex items-center justify-center bg-surface-muted text-xs text-content-muted px-2`}>
                      missing
                    </div>
                  )}
                  {!single && (
                    <span className="absolute top-1 left-1 bg-black/60 text-white text-xs rounded px-1.5 py-0.5">{i + 1}</span>
                  )}
                  <button
                    aria-label={`Remove ${label.toLowerCase()}`}
                    onClick={() => removeAt(i)}
                    disabled={saveMut.isPending}
                    className={`absolute top-1 right-1 opacity-0 group-hover:opacity-100 ${overlayBtn}`}
                  >✕</button>
                  {!single && (
                    <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100">
                      <button aria-label="Move left" disabled={i === 0 || saveMut.isPending} onClick={() => move(i, i - 1)} className={overlayBtn}>←</button>
                      <button aria-label="Move right" disabled={i === items.length - 1 || saveMut.isPending} onClick={() => move(i, i + 1)} className={overlayBtn}>→</button>
                    </div>
                  )}
                </div>
              );
            })}
            {items.length === 0 && (
              <p className="text-content-muted text-sm self-center">
                {isDraft && liveImages.length > 0 ? 'All images will be removed on submit.' : 'Not set.'}
              </p>
            )}
          </div>

          {single ? (
            <>
              {imageType === 'featureGraphic' && (
                <div className="mb-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={bannerBusy}
                    disabled={bannerBusy || !listingText.title.trim()}
                    title={listingText.title.trim() ? 'Drawn with the app\'s brand pack, by Play\'s feature-graphic rules' : 'The locale needs an app name first'}
                    onClick={drawBanner}
                  >
                    {bannerBusy ? 'Drawing the banner…' : items.length > 0 ? 'Redraw with AI' : 'Draw with AI'}
                  </Button>
                </div>
              )}
              {items.length < max && (
                <div className="max-w-xs">
                  <MediaUpload value="" onChange={() => {}} onUpload={onUpload} accept="image/*" mediaType="image" />
                </div>
              )}
            </>
          ) : (
            // Localize, generate and copy stay open on a full locale: each of
            // them can replace the set, only a plain upload needs a free slot.
            <div>
              <CopyScreenshotsBar
                lang={lang}
                sources={copySources}
                ownCount={items.length}
                otherLocales={otherLocales}
                defaultMode="replace"
                onCopy={(input) => copyScreenshots(appId, input)}
                onDone={invalidateDrafts}
              />
              <div className="flex flex-wrap gap-2 mb-3">
                {localizeSource.source && localizeSource.items.length > 0 && (
                  <Button size="sm" variant="secondary" onClick={() => setShowLocalize(true)}>
                    Localize screenshots from {localizeSource.source}
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => setShowGenerate(true)}>
                  {items.length >= max ? 'Generate a new set (AI)' : 'Generate screenshots (AI)'}
                </Button>
              </div>
              {localizeSource.source && localizeSource.items.length > 0 && (
                <LocalizeScreenshotsModal
                  isOpen={showLocalize}
                  appId={appId}
                  sourceLang={localizeSource.source}
                  targetLang={lang}
                  sourceItems={localizeSource.items}
                  existingTarget={localizeTarget}
                  onClose={() => setShowLocalize(false)}
                  onDone={() => { setShowLocalize(false); invalidateDrafts(); }}
                />
              )}
              <GenerateScreenshotsModal
                isOpen={showGenerate}
                target={shotTarget}
                onClose={() => setShowGenerate(false)}
                onDone={invalidateDrafts}
              />
              {items.length < max && (
                <div className="max-w-xs">
                  <MultiScreenshotUpload remaining={max - items.length} onFiles={onFiles} uploading={batchProgress} />
                </div>
              )}
            </div>
          )}
        </>
      )}
      {(liveError || actionError) && (
        <p className="text-red-400 text-sm mt-2">{actionError ?? (liveError as Error).message}</p>
      )}

      <ConfirmModal
        isOpen={confirmRevert}
        title="Revert to live"
        danger
        confirmLabel="Revert"
        loading={revertMut.isPending}
        message={`Discard the ${label.toLowerCase()} draft? Uploaded draft images will be deleted; the live images stay as they are.`}
        onConfirm={() => revertMut.mutate()}
        onClose={() => setConfirmRevert(false)}
      />
    </div>
  );
}

function MultiScreenshotUpload({ remaining, onFiles, uploading }: {
  remaining: number;
  onFiles: (files: File[]) => void;
  uploading: { done: number; total: number } | null;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const take = (list: FileList | null) => {
    if (!list) return;
    onFiles(Array.from(list));
  };
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
      onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files); }}
      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer text-sm transition-colors ${
        dragging ? 'border-primary bg-primary/5' : 'border-border-strong'
      } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*"
        className="hidden"
        onChange={(e) => { take(e.target.files); e.target.value = ''; }}
      />
      {uploading
        ? <span className="text-content-muted">Uploading {uploading.done}/{uploading.total}…</span>
        : <span className="text-content-muted">
            <span className="font-medium text-primary">Click to upload</span> or drop images — up to {remaining} more
          </span>}
    </div>
  );
}
